import { Link } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF, BOOKING_URL } from "@/data/site";

const LINK_COLUMNS = [
  {
    title: "Company",
    links: [
      { testid: "footer-about-link", to: "/about", label: "About Us" },
      { testid: "footer-areas-link", to: "/service-areas", label: "Service Areas" },
      { testid: "footer-faqs-link", to: "/faqs", label: "FAQs" },
      { testid: "footer-contact-link", to: "/contact", label: "Contact" },
    ],
  },
  {
    title: "Services",
    links: [
      { testid: "footer-services-link", to: "/services", label: "All Services & Pricing" },
      { testid: "footer-plans-link", to: "/maintenance-plans", label: "Maintenance Plans" },
      { testid: "footer-commercial-link", to: "/commercial", label: "Commercial" },
      { testid: "footer-condo-link", to: "/condo-associations", label: "Condo Association Program" },
      { testid: "footer-book-link", href: BOOKING_URL, label: "Book Online" },
    ],
  },
  {
    title: "Service Areas",
    links: [
      ...[["Miami", "miami"], ["Miami Gardens", "miami-gardens"], ["North Miami Beach", "north-miami-beach"], ["Hialeah", "hialeah"], ["Fort Lauderdale", "fort-lauderdale"], ["West Palm Beach", "west-palm-beach"]]
        .map(([label, slug]) => ({ testid: `footer-city-${slug}`, to: `/areas/${slug}`, label })),
      { testid: "footer-all-areas-link", to: "/service-areas", label: "All Service Areas", accent: true },
    ],
  },
];

const FooterColumn = ({ title, links }) => (
  <div>
    <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">{title}</h3>
    <ul className="space-y-2 text-sm">
      {links.map((l) => (
        <li key={l.testid}>
          {l.href ? (
            <a data-testid={l.testid} href={l.href} target="_blank" rel="noopener noreferrer" className="text-zinc-300 hover:text-white transition-colors duration-200">{l.label}</a>
          ) : (
            <Link data-testid={l.testid} to={l.to} className={`${l.accent ? "text-brand-ice" : "text-zinc-300"} hover:text-white transition-colors duration-200`}>{l.label}</Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const FooterBrand = () => (
  <div>
    <div className="mb-4">
      <img src="/logo-horizontal.webp" alt="Central Air Depot — AC Service, Install, Maintenance" className="h-12 w-auto" />
    </div>
    <p className="text-sm font-medium text-white italic">"Let us upgrade the air you breathe."</p>
    <p data-testid="footer-commercial-residential" className="text-xs tracking-[0.2em] uppercase font-bold text-brand-ice mb-2 mt-1">Commercial &amp; Residential</p>
    <p className="text-sm text-zinc-400 leading-relaxed">South Florida's trusted air conditioning service and maintenance company since 1998.</p>
  </div>
);

const FooterContact = () => (
  <div>
    <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">Contact</h3>
    <ul className="space-y-3 text-sm">
      <li><a data-testid="footer-phone-link" href={PHONE_HREF} className="flex items-center gap-2 text-zinc-300 hover:text-white transition-colors duration-200"><Phone className="w-4 h-4" /> {PHONE}</a></li>
      <li><a data-testid="footer-email-link" href={EMAIL_HREF} className="flex items-center gap-2 text-zinc-300 hover:text-white transition-colors duration-200"><Mail className="w-4 h-4" /> {EMAIL}</a></li>
      <li className="text-zinc-400">24/7 emergency booking. Call any time — our system takes your request, and a technician is dispatched after a $200 prepaid emergency booking fee, which is applied toward your repair.<br />Miami-Dade · Broward · Palm Beach</li>
    </ul>
  </div>
);

const FooterBottom = () => (
  <div className="border-t border-zinc-800">
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
      <p data-testid="footer-disclaimer" className="text-xs text-zinc-400 leading-relaxed">
        Central Air Depot is a booking and customer service platform. Services are performed by independent, state-licensed and insured HVAC contractors. The performing contractor's name and license number appear on your booking confirmation and invoice. New system installations are performed by Sunny Side Air Conditioning, Corp., FL License CAC1818033.
      </p>
      <div className="flex flex-col sm:flex-row justify-between gap-2 text-xs text-zinc-500">
        <span>© 2026 Central Air Depot. All rights reserved.</span>
        <span>Cooling South Florida since 1998.</span>
      </div>
    </div>
  </div>
);

export const Footer = () => (
  <footer className="bg-brand-ink text-white">
    <div className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 md:grid-cols-5 gap-10">
      <FooterBrand />
      {LINK_COLUMNS.map((c) => <FooterColumn key={c.title} title={c.title} links={c.links} />)}
      <FooterContact />
    </div>
    <FooterBottom />
  </footer>
);
